import { useEffect, useState } from 'react';
import { getStats, getTrades, getStatus, getBalance } from '../api';
import type { StatsResponse, TradesResponse, StatusResponse, BalanceResponse } from '../types';

interface ScorecardMetrics {
  totalTrades: number;
  winRate: number;
  profitFactor: number;
  avgWin: number;
  avgLoss: number;
  maxDrawdown: number;
  sharpeRatio: number;
  uptime: number;
  fillRate: number;
  errorCount: number;
}

interface ReadinessScore {
  score: number;
  grade: 'A' | 'B' | 'C' | 'D' | 'F';
  factors: {
    name: string;
    value: string | number;
    status: 'pass' | 'warn' | 'fail';
    weight: number;
  }[];
  recommendation: string;
}

export default function Scorecard() {
  const [metrics, setMetrics] = useState<ScorecardMetrics | null>(null);
  const [readiness, setReadiness] = useState<ReadinessScore | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        const [stats, trades, status, balance] = await Promise.all([
          getStats(),
          getTrades(50),
          getStatus(),
          getBalance(),
        ]);

        // Calculate metrics
        const calculated = calculateMetrics(stats, trades, status, balance);
        setMetrics(calculated);

        // Calculate readiness
        const readinessScore = calculateReadiness(calculated, status);
        setReadiness(readinessScore);
      } catch (e) {
        console.error('Failed to load scorecard:', e);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
    const id = setInterval(fetchData, 10000);
    return () => clearInterval(id);
  }, []);

  if (loading || !metrics || !readiness) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-trader-info"></div>
      </div>
    );
  }

  const gradeColor = {
    A: 'text-green-400',
    B: 'text-blue-400',
    C: 'text-yellow-400',
    D: 'text-orange-400',
    F: 'text-red-400',
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Live Trading Scorecard</h2>
        <span className="text-xs text-trader-muted">Auto-refresh: 10s</span>
      </div>

      {/* Readiness Score */}
      <div className="bg-trader-card border border-trader-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-sm text-trader-muted uppercase tracking-wider">Live Trading Readiness</p>
            <p className={`text-5xl font-bold ${gradeColor[readiness.grade]}`}>
              {readiness.score}/100
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-trader-muted uppercase tracking-wider">Grade</p>
            <p className={`text-4xl font-bold ${gradeColor[readiness.grade]}`}>
              {readiness.grade}
            </p>
          </div>
        </div>
        <div className="bg-trader-bg rounded-full h-3 overflow-hidden">
          <div
            className={`h-full transition-all duration-500 ${
              readiness.score >= 80 ? 'bg-green-500' :
              readiness.score >= 60 ? 'bg-yellow-500' :
              readiness.score >= 40 ? 'bg-orange-500' : 'bg-red-500'
            }`}
            style={{ width: `${readiness.score}%` }}
          />
        </div>
        <p className="mt-3 text-sm text-trader-muted">{readiness.recommendation}</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-trader-card border border-trader-border rounded-lg p-4">
          <p className="text-xs text-trader-muted uppercase">Win Rate</p>
          <p className={`text-2xl font-bold ${metrics.winRate >= 50 ? 'text-green-400' : 'text-red-400'}`}>
            {metrics.winRate.toFixed(1)}%
          </p>
        </div>
        <div className="bg-trader-card border border-trader-border rounded-lg p-4">
          <p className="text-xs text-trader-muted uppercase">Profit Factor</p>
          <p className={`text-2xl font-bold ${metrics.profitFactor >= 1.5 ? 'text-green-400' : metrics.profitFactor >= 1 ? 'text-yellow-400' : 'text-red-400'}`}>
            {metrics.profitFactor.toFixed(2)}
          </p>
        </div>
        <div className="bg-trader-card border border-trader-border rounded-lg p-4">
          <p className="text-xs text-trader-muted uppercase">Sharpe Ratio</p>
          <p className={`text-2xl font-bold ${metrics.sharpeRatio >= 1 ? 'text-green-400' : metrics.sharpeRatio >= 0 ? 'text-yellow-400' : 'text-red-400'}`}>
            {metrics.sharpeRatio.toFixed(2)}
          </p>
        </div>
        <div className="bg-trader-card border border-trader-border rounded-lg p-4">
          <p className="text-xs text-trader-muted uppercase">Max Drawdown</p>
          <p className={`text-2xl font-bold ${metrics.maxDrawdown <= 10 ? 'text-green-400' : metrics.maxDrawdown <= 20 ? 'text-yellow-400' : 'text-red-400'}`}>
            {metrics.maxDrawdown.toFixed(1)}%
          </p>
        </div>
      </div>

      {/* Detailed Factors */}
      <div className="bg-trader-card border border-trader-border rounded-lg p-4">
        <h3 className="text-sm font-semibold mb-4">Readiness Factors</h3>
        <div className="space-y-3">
          {readiness.factors.map((factor, i) => (
            <div key={i} className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className={`w-2 h-2 rounded-full ${
                  factor.status === 'pass' ? 'bg-green-500' :
                  factor.status === 'warn' ? 'bg-yellow-500' : 'bg-red-500'
                }`} />
                <span className="text-sm">{factor.name}</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm font-mono">{factor.value}</span>
                <span className={`text-xs px-2 py-0.5 rounded ${
                  factor.status === 'pass' ? 'bg-green-500/20 text-green-400' :
                  factor.status === 'warn' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400'
                }`}>
                  {factor.status.toUpperCase()}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Strategy Recommendation */}
      <div className="bg-trader-card border border-trader-border rounded-lg p-4">
        <h3 className="text-sm font-semibold mb-3">Live Trading Strategy</h3>
        <div className="space-y-2 text-sm text-trader-muted">
          <p>Based on your paper trading performance:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            {readiness.score >= 80 && (
              <li className="text-green-400">✓ Ready for live trading with small position sizes (5-10% of capital)</li>
            )}
            {readiness.score >= 60 && readiness.score < 80 && (
              <li className="text-yellow-400">⚠ Continue paper trading for {Math.ceil((80 - readiness.score) / 10)} more days</li>
            )}
            {readiness.score < 60 && (
              <li className="text-red-400">✗ Not ready - address failing factors before going live</li>
            )}
            <li>Start with conservative position sizing: 1-2% risk per trade</li>
            <li>Set hard stop loss at 5% of portfolio</li>
            <li>Monitor closely for first 24 hours of live trading</li>
          </ul>
        </div>
      </div>

      {/* Timing Recommendation */}
      <div className="bg-trader-card border border-trader-border rounded-lg p-4">
        <h3 className="text-sm font-semibold mb-3">Best Time to Go Live</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="text-center p-3 bg-trader-bg rounded-lg">
            <p className="text-xs text-trader-muted mb-1">Minimum Trades</p>
            <p className={`text-lg font-bold ${metrics.totalTrades >= 50 ? 'text-green-400' : 'text-yellow-400'}`}>
              {metrics.totalTrades}/50
            </p>
          </div>
          <div className="text-center p-3 bg-trader-bg rounded-lg">
            <p className="text-xs text-trader-muted mb-1">Uptime Required</p>
            <p className={`text-lg font-bold ${metrics.uptime >= 24 ? 'text-green-400' : 'text-yellow-400'}`}>
              {metrics.uptime.toFixed(1)}/24h
            </p>
          </div>
          <div className="text-center p-3 bg-trader-bg rounded-lg">
            <p className="text-xs text-trader-muted mb-1">Win Rate Target</p>
            <p className={`text-lg font-bold ${metrics.winRate >= 45 ? 'text-green-400' : 'text-yellow-400'}`}>
              {metrics.winRate.toFixed(0)}%/45%
            </p>
          </div>
        </div>
        <p className="mt-4 text-sm text-trader-muted text-center">
          Current optimal window: {getOptimalWindow(metrics)}
        </p>
      </div>
    </div>
  );
}

function calculateMetrics(
  stats: StatsResponse,
  trades: TradesResponse,
  status: StatusResponse,
  balance: BalanceResponse
): ScorecardMetrics {
  const tradeList = trades.trades;
  const totalTrades = tradeList.length;
  
  // Calculate win rate and P&L
  let wins = 0, losses = 0;
  let totalWin = 0, totalLoss = 0;
  
  tradeList.forEach(t => {
    if (t.pnl > 0) {
      wins++;
      totalWin += t.pnl;
    } else if (t.pnl < 0) {
      losses++;
      totalLoss += Math.abs(t.pnl);
    }
  });

  const winRate = totalTrades > 0 ? (wins / totalTrades) * 100 : 0;
  const profitFactor = totalLoss > 0 ? totalWin / totalLoss : totalWin > 0 ? 999 : 0;
  const avgWin = wins > 0 ? totalWin / wins : 0;
  const avgLoss = losses > 0 ? totalLoss / losses : 0;

  // Calculate drawdown (simplified)
  let peak = 0, maxDd = 0;
  let equity = balance.net_pnl || 0;
  tradeList.forEach(t => {
    equity += t.pnl || 0;
    if (equity > peak) peak = equity;
    const dd = peak - equity;
    if (dd > maxDd) maxDd = dd;
  });
  const maxDrawdown = equity > 0 ? (maxDd / (peak || 1)) * 100 : 0;

  // Sharpe ratio (simplified)
  const returns = tradeList.map(t => t.pnl || 0);
  const avgReturn = returns.reduce((a, b) => a + b, 0) / (totalTrades || 1);
  const stdDev = Math.sqrt(returns.reduce((sq, n) => sq + Math.pow(n - avgReturn, 2), 0) / (totalTrades || 1));
  const sharpeRatio = stdDev > 0 ? (avgReturn / stdDev) * Math.sqrt(252) : 0;

  const uptime = status.uptime_seconds / 3600;
  const fillRate = stats.total_trades > 0 ? (stats.filled_trades / stats.total_trades) * 100 : 0;

  return {
    totalTrades,
    winRate,
    profitFactor,
    avgWin,
    avgLoss,
    maxDrawdown,
    sharpeRatio,
    uptime,
    fillRate,
    errorCount: 0,
  };
}

function calculateReadiness(metrics: ScorecardMetrics, status: StatusResponse): ReadinessScore {
  const factors: { name: string; value: string | number; status: 'pass' | 'warn' | 'fail'; weight: number }[] = [
    { name: 'Trade Count', value: `${metrics.totalTrades}/50`, status: metrics.totalTrades >= 50 ? 'pass' : metrics.totalTrades >= 20 ? 'warn' : 'fail', weight: 15 },
    { name: 'Win Rate', value: `${metrics.winRate.toFixed(1)}%`, status: metrics.winRate >= 45 ? 'pass' : metrics.winRate >= 35 ? 'warn' : 'fail', weight: 20 },
    { name: 'Profit Factor', value: metrics.profitFactor.toFixed(2), status: metrics.profitFactor >= 1.5 ? 'pass' : metrics.profitFactor >= 1 ? 'warn' : 'fail', weight: 20 },
    { name: 'Drawdown', value: `${metrics.maxDrawdown.toFixed(1)}%`, status: metrics.maxDrawdown <= 10 ? 'pass' : metrics.maxDrawdown <= 20 ? 'warn' : 'fail', weight: 15 },
    { name: 'Uptime', value: `${metrics.uptime.toFixed(1)}h`, status: metrics.uptime >= 24 ? 'pass' : metrics.uptime >= 6 ? 'warn' : 'fail', weight: 10 },
    { name: 'Fill Rate', value: `${metrics.fillRate.toFixed(0)}%`, status: metrics.fillRate >= 95 ? 'pass' : metrics.fillRate >= 80 ? 'warn' : 'fail', weight: 10 },
    { name: 'Kill Switch', value: status.kill_switch.active ? 'ACTIVE' : 'OK', status: !status.kill_switch.active ? 'pass' : 'fail', weight: 10 },
  ];

  let score = 0;
  factors.forEach(f => {
    if (f.status === 'pass') score += f.weight;
    else if (f.status === 'warn') score += f.weight * 0.5;
  });

  let grade: ReadinessScore['grade'];
  if (score >= 80) grade = 'A';
  else if (score >= 60) grade = 'B';
  else if (score >= 40) grade = 'C';
  else if (score >= 20) grade = 'D';
  else grade = 'F';

  let recommendation = '';
  if (grade === 'A') recommendation = 'Ready for live trading! Start with conservative position sizes.';
  else if (grade === 'B') recommendation = 'Almost ready. Continue monitoring for better confidence.';
  else if (grade === 'C') recommendation = 'More testing recommended. Consider running in paper mode for extended period.';
  else recommendation = 'Not ready. Address failing factors before live trading.';

  return { score, grade, factors, recommendation };
}

function getOptimalWindow(_metrics: ScorecardMetrics): string {
  const hour = new Date().getHours();
  
  // Crypto markets are 24/7 but volatility patterns vary
  if (hour >= 9 && hour <= 11) return 'Morning (9-11 AM UTC) - Lower volatility';
  if (hour >= 13 && hour <= 15) return 'Afternoon (1-3 PM UTC) - Higher volume';
  if (hour >= 20 && hour <= 23) return 'Evening (8-11 PM UTC) - Best liquidity';
  
  return 'Continue paper trading until optimal window';
}
